import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerSection,
  DrawerDetailRow,
  DrawerFooter,
} from '@/components/ui/drawer'
import { getFloorplanById } from '@/api/floorplans.api'
import type { Floorplan } from '@/types/api'

interface FloorplanDetailDrawerProps {
  floorplanId: string | null
  onClose: () => void
  onUpdate?: (floorplan: Floorplan) => void
  showUnitColumn: boolean
  onDownload?: (url: string, fileName: string) => void
}

export function FloorplanDetailDrawer({
  floorplanId,
  onClose,
  onUpdate,
  onDownload,
  showUnitColumn,
}: FloorplanDetailDrawerProps) {
  const { data: floorplan, isLoading } = useQuery({
    queryKey: ['floorplan-detail', floorplanId],
    queryFn: () =>
      floorplanId ? getFloorplanById(floorplanId) : Promise.resolve(null),
    enabled: !!floorplanId,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  const getDisplayFileName = (urlStr: string) => {
    if (floorplan?.fileInfo?.originFileName) {
      return floorplan.fileInfo.originFileName
    }
    if (!urlStr) return ''
    try {
      const cleanUrl = urlStr.split('?')[0]
      return cleanUrl.split('/').pop() || 'image.png'
    } catch {
      return 'image.png'
    }
  }

  const actualImage = floorplan?.fileInfo?.fileUrl
  const actualDescription = floorplan?.description

  return (
    <Drawer
      open={!!floorplanId}
      onClose={onClose}
      title="CHI TIẾT SƠ ĐỒ"
      styles={{ body: { overflow: 'auto' } }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            className="min-w-[160px] h-10"
          >
            Đóng
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => floorplan && onUpdate?.(floorplan)}
            className="min-w-[160px] h-10"
          >
            Cập nhật
          </Button>
        </DrawerFooter>
      }
    >
      {isLoading && (
        <div className="p-6 text-center text-gray-500">
          Đang tải chi tiết...
        </div>
      )}

      {floorplan && (
        <div>
          <DrawerSection>
            <DrawerDetailRow label="Tên sơ đồ:" value={floorplan.name} />
            {showUnitColumn && (
              <DrawerDetailRow
                label="Đơn vị:"
                value={
                  floorplan.unitName ||
                  'Ban Quản lý các Khu công nghiệp tỉnh Đồng Nai'
                }
              />
            )}
            <DrawerDetailRow
              label="Sơ đồ:"
              value={
                actualImage ? (
                  <button
                    type="button"
                    className="cursor-pointer text-[#2196F3] underline hover:no-underline"
                    onClick={() => {
                      const name = getDisplayFileName(actualImage)
                      onDownload?.(actualImage, name)
                    }}
                  >
                    {getDisplayFileName(actualImage)}
                  </button>
                ) : (
                  <span className="text-gray-500 italic">Chưa có sơ đồ</span>
                )
              }
            />
            <DrawerDetailRow
              label="Chiều rộng:"
              value={floorplan.width != null ? `${floorplan.width} m` : '-'}
            />

            <DrawerDetailRow
              label="Chiều dài:"
              value={floorplan.height != null ? `${floorplan.height} m` : '-'}
            />
            <DrawerDetailRow
              label="Ghi chú:"
              value={actualDescription || 'Không có ghi chú'}
              alignTop
            />
          </DrawerSection>
        </div>
      )}
    </Drawer>
  )
}
